import { html } from "lit";
import { msg } from "@omegagrid/localize";
import { MenuItem } from "@omegagrid/core";
import { NodeModel, NodeSource, TreeSource } from "@omegagrid/tree";
import rp, { BrowserAdapter, BrowserOptions, helpers } from "@reporting/rp-app-host";
import { DbTable, DbView, DbProcedure, DbFunction } from "@reporting/rp-types/server";
import { DwhObjectNodeProps, buildIndexScriptId } from "../types";

type ExportFormat = 'csv';

export class DWHBrowserAdapter extends BrowserAdapter {

	getTooltip(node: NodeModel) {
		const props = node.properties as DwhObjectNodeProps | undefined;
		if (!props || props.type !== 'table') return null;
		return html`
			<div>
				${msg('Primary key')}: ${props.has_primary_key ? msg('Yes') : msg('No')}<br>
				${msg('Indexes')}: ${props.indexes?.length ?? 0}<br>
				${msg('Triggers')}: ${props.triggers?.length ?? 0}
			</div>
		`;
	}

	async getTree(): Promise<TreeSource> {
		const [tables, views, procedures, functions] = await Promise.all([
			rp.ms('server').get<DbTable[]>('tables'),
			rp.ms('server').get<DbView[]>('views'),
			rp.ms('server').get<DbProcedure[]>('procedures'),
			rp.ms('server').get<DbFunction[]>('functions'),
		]);

		const items: NodeSource[] = [];

		// Tables folder
		if (tables.length) {
			items.push({
				k: 'tables',
				v: msg('Tables'),
				l: 0,
				collapsed: true,
				folder: BrowserAdapter.folderOptions,
			});
			for (const table of tables) {
				items.push({
					k: `dwh-values-table-${table.object_id}`,
					v: table.table_name,
					l: 1,
					s: false,
					icon: { icon: 'table' },
					props: {
						type: 'table',
						name: table.table_name,
						object_id: table.object_id,
						has_primary_key: table.has_primary_key,
						indexes: table.indexes ?? [],
						triggers: table.triggers ?? [],
					} as DwhObjectNodeProps,
				});
			}
		}

		// Views folder
		if (views.length) {
			items.push({
				k: 'views',
				v: msg('Views'),
				l: 0,
				collapsed: true,
				folder: BrowserAdapter.folderOptions,
			});
			for (const view of views) {
				items.push({
					k: `dwh-values-view-${view.object_id}`,
					v: view.view_name,
					l: 1,
					s: false,
					icon: { icon: 'eye' },
					props: {
						type: 'view',
						name: view.view_name,
						object_id: view.object_id,
					} as DwhObjectNodeProps,
				});
			}
		}

		// Procedures folder
		if (procedures.length) {
			items.push({
				k: 'procedures',
				v: msg('Procedures'),
				l: 0,
				collapsed: true,
				folder: BrowserAdapter.folderOptions,
			});
			for (const proc of procedures) {
				items.push({
					k: `dwh-sql-procedure-${proc.object_id}`,
					v: proc.name,
					l: 1,
					icon: { icon: 'gears', statusIcon: proc.encrypted ? 'lock' : null },
				});
			}
		}

		// Functions folder
		if (functions.length) {
			items.push({
				k: 'functions',
				v: msg('Functions'),
				l: 0,
				collapsed: true,
				folder: BrowserAdapter.folderOptions,
			});
			for (const fn of functions) {
				items.push({
					k: `dwh-sql-function-${fn.object_id}`,
					v: fn.name,
					l: 1,
					icon: { icon: 'function' },
				});
			}
		}

		// Processes leaf
		items.push({
			k: 'dwh-processes',
			v: msg('Processes'),
			l: 0,
			icon: { icon: 'list-check' },
		});

		return items;
	}

	async getMenuItems(node: NodeModel): Promise<MenuItem[]> {
		const items: MenuItem[] = [];
		const props = node.properties as DwhObjectNodeProps | undefined;
		if (!props || node.folder) return items;

		// table actions
		if (props.type === 'table' && props.object_id != null) {
			items.push({
				key: 'export-csv',
				value: `${msg('Export')} - CSV`,
				icon: 'file-csv',
				divider: (props.indexes?.length || props.triggers?.length) > 0,
			});

			if (props.indexes?.length) {
				items.push({ key: '__label_idx', value: msg('Indexes'), type: 'label' });
				for (const idx of props.indexes) {
					items.push({
						key: `index-${idx.index_id}`,
						value: idx.name,
						icon: { icon: 'key', statusIcon: idx.is_unique ? 'lock' : null } as any,
					});
				}
			}

			if (props.triggers?.length) {
				items.push({ key: '__label_trg', value: msg('Triggers'), type: 'label' });
				for (const trg of props.triggers) {
					items.push({
						key: `trigger-${trg.object_id}`,
						value: trg.name,
						icon: 'bolt',
					});
				}
			}
		}

		// view actions
		if (props.type === 'view' && props.object_id != null) {
			items.push({ key: 'export-csv', value: `${msg('Export')} - CSV`, icon: 'file-csv', divider: true });
			items.push({ key: 'view-script', value: msg('Definition'), icon: 'code' });
		}

		return items;
	}

	getOptions(): Partial<BrowserOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			search: false,
			contextMenuButton: true,
		});
	}

	async onAction(key: string, node: NodeModel) {
		const props = node.properties as DwhObjectNodeProps | undefined;
		if (!props) return;

		if (key === 'export-csv') {
			if (props.object_id != null) {
				await this.exportObject(props.object_id, props.name);
			}
			return;
		}

		if (key === 'view-script' && props.type === 'view' && props.object_id != null) {
			rp.host.open(`dwh-sql-view-${props.object_id}`, {
				title: `${msg('View')}: ${props.name}`,
			});
			return;
		}

		if (key.startsWith('index-') && props.type === 'table' && props.object_id != null) {
			const indexId = parseInt(key.substring('index-'.length));
			const idx = props.indexes?.find(i => i.index_id === indexId);
			if (!idx) return;
			rp.host.open(`dwh-sql-index-${buildIndexScriptId(props.object_id, indexId)}`, {
				title: `${msg('Index')}: ${idx.name}`,
			});
			return;
		}

		if (key.startsWith('trigger-')) {
			const triggerId = parseInt(key.substring('trigger-'.length));
			const trg = props.triggers?.find(t => t.object_id === triggerId);
			if (!trg) return;
			rp.host.open(`dwh-sql-trigger-${triggerId}`, {
				title: `${msg('Trigger')}: ${trg.name}`,
			});
			return;
		}
	}

	private async exportObject(objectId: number, name: string) {
		await rp.ms('server').download(`export/${objectId}`, {
			fileName: helpers.createExportFilename('csv'),
			csvParams: { header: true, delimiter: ',', encoding: 'utf-8' },
			format: 'csv',
		});
	}

}
